import { createContext } from "react";
import type { User } from "@/features/auth/services/firebase";
import type { CompanyProfileData } from "@/services/api/user-client";

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  onboardingCompleted: boolean;
  teams: Array<{
    id: string;
    name: string;
    role: string;
    /** True for the user's single auto-created personal team (hidden in the
     * workspace switcher — the "personal" scope already represents it). */
    isPersonal?: boolean;
  }>;
  appLanguage?: "pl" | "en";
  reportLanguage?: "pl" | "en";
  /** True for internal/team accounts only (docs/specs/
   * settings-internal-testing-tools.md) — gates the Settings "Testing tools"
   * tab. UX-only; every internal endpoint re-checks server-side. */
  isInternalTester?: boolean;
  /** Onboarded company profile (null when the user has not finished
   * onboarding or has no associated business). Consumed by
   * `useSettingsProfile` to seed the settings form from `useAuth()` instead of
   * a duplicate `GET /auth/me` on mount (audit 2026-08-25 D.8). */
  companyData?: CompanyProfileData | null;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  isConfigured: boolean;
  accountDisabled: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name?: string,
    signupSource?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getToken: () => Promise<string | null>;
  profile: UserProfile | null;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);
